import React from 'react';
import { ApplicationState } from 'store/rootReducer';
import {
  createServiceThunk,
  deleteService,
  exportService,
  fetchServices,
  importService,
} from 'store/services/servicesActions';
import { connect } from 'react-redux';
import Dropdown from 'react-bootstrap/Dropdown';
import { FormControl, OverlayTrigger, Popover } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import ServiceToggle from 'components/ui/servicePicker/toggle/ServiceToggle';
import SimpleLoader from 'components/loaders/SimpleLoader';
import { getShortString } from 'utils/miscUtil';
import { ServicesState } from 'store/services/servicesTypes';
import { WithTranslation, withTranslation } from 'react-i18next';
import i18n from 'i18n';
import { Service } from 'types/rest/assetCatalogTypes';
import clsx from 'clsx';
import EditServiceModal from 'components/ui/servicePicker/EditServiceModal';
import { confirm } from 'components/ui/modal/BlazeConfirmationModal';
import _ from 'lodash';
import styles from './ServicePicker.module.css';
import BzmIcon from 'components/icons/BzmIcon';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { handleFieldChange } from 'utils/mockServiceUtils';
import { isServiceProtected } from 'utils/bzmUserConfigUtil';
import CloneServiceModal from './CloneServiceModal';
import { formatDateTime } from '../../../utils/dateUtil';
import { DEFAULT_POPPER_CONFIG } from '../../../constants/ui-constants';
import { copyToClipboard } from '../../buttons/CopyToClipboardButton';
import MixpanelService from '../../../utils/mixpanelService';
import { EXPORT_SERVICE_TO_ZIP, IMPORT_SERVICE_FROM_ZIP } from '../../../utils/mixpanelEvents';

interface MyServiceIdIconClicked {
  key: number;
  value: string;
}

interface LocalState {
  /** Selected service */
  selected: Service | null;
  /** Term used for filtering services */
  searchTerm: string;
  /** Used for determining whether to show create Service button */
  fullMatchFound: boolean;
  /** Default text in the select input*/
  selectPlaceholder: string;
  isServiceNameValid: boolean;
  invalidErrorMessage: string;
  /** service modal open **/
  isServiceModalOpen: boolean;
  /** Selected service for actions on modal */
  modalSelected: Service | null;
  /** Create new service modal */
  newServiceModal: boolean;
  /** custom dropdown visibility */
  keepDropdownOpen: boolean | undefined;
  /** Selected service icon */
  serviceIconSelected: MyServiceIdIconClicked | undefined;
  /** Prexis for Icons ids*/
  itemIdPrefix: string;
  /** click events*/
  clonedServiceName: string;
  clickOnPopoverEvent: boolean;
  isCloneServiceModalOpen: boolean;
  importedService: null | Service;
}

interface ServicePickerProps {
  /** Whether or not display list of all virtual services */
  isSelectAll?: boolean;
  /** Id of the vs service to be selected */
  preSelected?: number;
  /** Change handler */
  selectHandler: (vs: Service | null) => void;
  /** Allow user to create new service*/
  isCreateEnabled?: boolean;
  /** Allow user to edit/delete service*/
  isMaintenanceEnabled?: boolean;
  /** Id of the element */
  id?: string;
  /** Disables the auto-loading of services when component mounts.  Useful if this component is used in a list. */
  manualPreLoad?: boolean;
  /** Disables selection of the service. */
  selectionDisabled?: boolean;
  /** Max height*/
  maxHeight?: string;
  /** Override of the default popper config*/
  popperConfig?: any;
  /** Allow user to clone service*/
  isCloneEnabled?: boolean;
  copyServiceIdCapability?: boolean;
  focus?: boolean;
  /** When provided, only services with these IDs will appear in the dropdown */
  filterServiceIds?: number[];
  /** When provided, renders a "create blueprint" icon on each service row. */
  onCreateBlueprint?: (service: Service) => void;
}

type DispatchProps = {
  fetchVirtualServices: (forceRefresh?: boolean) => Promise<void>;
  createService: (serviceName: string) => Promise<Service>;
  deleteService: (services: Service[], service: Service, selectedServiceId: number | undefined) => void;
  exportService: (services: Service[], service: Service) => void;
  importService: (service: Service, file: File) => void;
  showToastNotification: (type: ToastType, message: string) => void;
};
type MapToStateProps = ReturnType<typeof mapStateToProps>;

// Combine both local + state + dispatch props - as well as any props we want to pass - in a union type.
type AllProps = ServicePickerProps & MapToStateProps & ServicesState & DispatchProps & WithTranslation;

/**
 * Service picker with search and Service creation button.
 */
class ServicePicker extends React.Component<AllProps, LocalState> {
  private SERVICE_NAME_MAX_LENGTH = 250;
  private readonly searchInputRef: React.RefObject<any> = React.createRef();
  private readonly fileUploadRef: React.RefObject<any> = React.createRef();

  pickerMenuStyles = {
    border: '0px',
    minWidth: '340px',
    width: '100%',
    padding: '15px',
    maxWidth: '250px',
  };

  constructor(props: AllProps) {
    super(props);
    this.searchInputRef = React.createRef();

    const { t } = this.props;
    //Default state
    this.state = {
      selected: null,
      searchTerm: '',
      fullMatchFound: false,
      selectPlaceholder:
        props.isSelectAll === true
          ? t('servicePicker.allServicesPlaceholder')
          : t('servicePicker.selectServicePlaceholder'),
      isServiceNameValid: true,
      invalidErrorMessage: '',
      isServiceModalOpen: false,
      modalSelected: null,
      newServiceModal: false,
      serviceIconSelected: undefined,
      keepDropdownOpen: undefined,
      itemIdPrefix: _.uniqueId('icon'),
      clickOnPopoverEvent: false,
      isCloneServiceModalOpen: false,
      clonedServiceName: '',
      importedService: null,
    };
  }

  /**
   * Gets focus on the search by ref.
   */
  handleFocus = () => {
    if (this.searchInputRef.current != null) {
      setTimeout(() => {
        this.searchInputRef.current.focus();
      }, 100);
    }
  };

  /**
   * Fetch services once component is visible.
   */
  componentDidMount() {
    if (!this.props.manualPreLoad) {
      this.props.fetchVirtualServices().then(() => {
        // Reset search
        this.setState({ searchTerm: '' });

        // Set preselected service
        if (this.props.preSelected != null) {
          this.preselectServiceById(this.props.preSelected);
        }
      });
    } else {
      // Set preselected service
      if (this.props.preSelected != null) {
        this.preselectServiceById(this.props.preSelected);
      }
    }
  }

  componentDidUpdate(prevProps: Readonly<AllProps>) {
    if (!_.isEqual(prevProps.services, this.props.services)) {
      if (this.state.selected !== null) {
        const selectedId = this.state.selected.id;
        const serviceUpdated: Service = _.filter(this.props.services, (service: any) => service.id === selectedId)[0];
        if (serviceUpdated !== null) {
          this.setState({ selected: serviceUpdated });
        }
      }
    }
  }

  //Need this here to fix a bug where the drop-down value doesn't update correctly when this is used in a list
  UNSAFE_componentWillReceiveProps(nextProps: Readonly<AllProps>): void {
    if (nextProps.preSelected != null) {
      this.preselectServiceById(nextProps.preSelected);
    }
  }

  /**
   * Gets array of filtered services by search keyword.
   */
  getFilteredServices = (): Service[] => {
    let filteredServices: Service[] = [];
    if (this.props.services != null && this.state.isServiceNameValid) {
      filteredServices = this.props.services.filter(service => {
        const matchesSearch =
          this.state.searchTerm !== ''
            ? service.name.toLocaleLowerCase().includes(this.state.searchTerm.toLowerCase())
            : true;
        const matchesFilter = this.props.filterServiceIds
          ? service.id != null && this.props.filterServiceIds.includes(service.id)
          : true;
        return matchesSearch && matchesFilter;
      });
    }
    return filteredServices;
  };

  /**
   * Search input field value has changed handler.
   * @param event
   */
  searchChangedHandler = (event: any) => {
    if (this.state.serviceIconSelected !== undefined) {
      this.handlePopover(this.state.serviceIconSelected.value);
    }

    const elements = handleFieldChange(event, this.SERVICE_NAME_MAX_LENGTH, this.props.services);
    this.setState({
      searchTerm: elements.searchTerm,
      fullMatchFound: elements.fullMatchFound,
      isServiceNameValid: elements.isServiceNameValid,
      invalidErrorMessage: elements.invalidErrorMessage,
    });
  };

  /**
   * Selects service.
   */
  selectService = (event: any, service: Service | null) => {
    this.setState({ selected: service });
    this.props.selectHandler(service);
  };

  /**
   * Preselects service by id.
   * @param id
   */
  preselectServiceById = (id: number) => {
    const service: Service | undefined = this.props.services.find(service => {
      if (service.id != null) {
        return service.id === id;
      }
      return false;
    });

    if (service !== undefined) {
      this.setState({ selected: service });
    } else {
      this.setState({ selected: null });
    }
  };

  createServiceCallBack = (service: Service) => {
    // Reset search
    this.setState({ searchTerm: '' }, () => {
      //Click outside closes the dropdown window
      document.dispatchEvent(new MouseEvent('click'));

      //Refresh all virtual services so our new created one is available
      this.props.fetchVirtualServices(true).then(() => {
        //When service is created, select it
        this.selectService(null, service);
      });
    });
  };

  /**
   * Triggers create new service event.
   */
  createService = () => {
    // open New Service Modal when nothing was written in searchTerm or searchTerm matched an existing service
    if (this.state.searchTerm.trim().length === 0 || (this.state.searchTerm !== '' && this.state.fullMatchFound)) {
      this.setState({
        isServiceModalOpen: true,
        newServiceModal: true,
        keepDropdownOpen: false,
      });
    } else {
      // otherwise call directly create service action with content from searchTerm field
      this.props
        .createService(this.state.searchTerm.trim())
        .then((service: Service) => this.createServiceCallBack(service))
        .catch(() => {
          console.error('Failed to create Service');
        });
    }
  };

  /**
   * Creates footer which reflects different scenarios.
   *
   * @param serviceItems serviceItem elements shown in the render.
   */
  createDropDownFooter = (serviceItems: any) => {
    const { t } = this.props;
    let footer;

    if (!this.state.isServiceNameValid) {
      return (
        <div className="invalid-feedback">{`${i18n.t('servicePicker.serviceNameIsInvalid')} ${
          this.state.invalidErrorMessage
        }`}</div>
      );
    }

    // Service picker allows to create new services
    if (this.props.isCreateEnabled === true) {
      footer = this.createDropDownFooterWhenCreationEnabled();
    }

    // Select mode only
    else {
      //User is doing a search and there are services available
      if (this.state.searchTerm !== '' && serviceItems.length === 0) {
        footer = (
          <div className="text-center">
            <hr />
            <p>
              {t('servicePicker.service')}{' '}
              <b className="text-break" title={this.state.searchTerm}>
                {this.state.searchTerm}
              </b>{' '}
              {t('servicePicker.serviceNotFound')}
            </p>
          </div>
        );
      }

      // There are no services
      if (this.state.searchTerm === '' && serviceItems.length === 0) {
        footer = <div className="text-center">{t('servicePicker.noServicesAvailable')}</div>;
      }
    }

    return footer;
  };

  /**
   * Creates dropdown footer for case creation is enabled.
   */
  createDropDownFooterWhenCreationEnabled = () => {
    const { t } = this.props;
    let creatableFooter;

    // There are some data in the backend
    if (this.props.services.length > 0) {
      // There is no service matching the search, allow user to create it
      if (this.state.searchTerm !== '' && !this.state.fullMatchFound) {
        creatableFooter = (
          <div className="text-center">
            <hr />
            <p>
              {t('servicePicker.service')}{' '}
              <b className="text-break" title={this.state.searchTerm}>
                {this.state.searchTerm}
              </b>{' '}
              {t('servicePicker.serviceNotFound')}
              <span>{t('servicePicker.doYouWantToAddIt')}</span>
            </p>
            <Button onClick={this.createService} className="qa-create-service">
              {t('servicePicker.addService')}
            </Button>
          </div>
        );
      } else {
        creatableFooter = (
          <div className="text-center">
            <hr />
            <Button onClick={this.createService} className="qa-create-service">
              {t('servicePicker.addService')}
            </Button>
          </div>
        );
      }
    } else {
      creatableFooter = (
        <div className="text-center">
          <hr />
          <p>{t('servicePicker.noServicesAvailableCreateOne')}</p>
          <Button onClick={this.createService} className="qa-create-service" disabled={this.state.searchTerm === ''}>
            {t('servicePicker.addService')}
          </Button>
        </div>
      );
    }

    return creatableFooter;
  };

  deleteServiceHandler = (service: Service) => {
    if (this.state.serviceIconSelected !== undefined) {
      this.handlePopover(this.state.serviceIconSelected.value);
    }
    this.setState({ keepDropdownOpen: false });
    const modalBody = (
      <p>
        {i18n.t('servicePicker.deleteConfirmationInit')} <b className="text-break">{service.name}</b>?{' '}
        {i18n.t('servicePicker.deleteConfirmationEnd')}
      </p>
    );
    confirm({
      confirmation: modalBody,
      title: i18n.t('servicePicker.deleteService'),
      yesOptionTitle: i18n.t('common:actions.delete'),
    })
      .then(() => {
        const selectedServiceId = this.state.selected !== null ? this.state.selected.id : undefined;
        this.props.deleteService(this.props.services, service, selectedServiceId);
      })
      .finally(() => {
        this.postModalClose(false);
      });
  };

  editService = (event: any, service: Service) => {
    if (this.state.serviceIconSelected !== undefined) {
      this.handlePopover(this.state.serviceIconSelected.value);
    }
    this.setState({
      modalSelected: service,
      isServiceModalOpen: true,
      keepDropdownOpen: false /*, clickOnEditEvent: true,*/ /*keepDropdownOpen:true*/,
    });
  };

  handlePopover = (iconId: string) => {
    const servicePopover = document.getElementById(iconId);
    if (servicePopover !== null) {
      servicePopover.click();
    }
  };

  getServiceIdDom = (serviceId: number) => {
    if (this.props.copyServiceIdCapability === false) {
      return null;
    }
    const iconId = this.state.itemIdPrefix + '_' + serviceId;
    return (
      <>
        <OverlayTrigger
          trigger={'click'}
          key="22"
          placement="bottom"
          popperConfig={DEFAULT_POPPER_CONFIG}
          overlay={
            <Popover
              id={'popoverId'}
              className={`popover-positioned-right`}
              title={i18n.t('servicePicker.serviceId')}
              onClick={() => {
                this.setState({ keepDropdownOpen: true, clickOnPopoverEvent: true });
              }}
            >
              <div className={'text-left p-3'}>
                <span>{serviceId}</span>
                <Button
                  className="btn-link btn-no-hover ml-2"
                  style={{ border: 0 }}
                  variant={'secondary'}
                  title={i18n.t('servicePicker.copyServiceId')}
                  onClick={(event: any) => {
                    event.preventDefault();
                    event.stopPropagation();
                    copyToClipboard(serviceId.toString(), i18n.t('servicePicker.copyDataSuccessMsg'));
                    this.handlePopover(iconId);
                  }}
                >
                  <BzmIcon className="icon-xs text-primary " variant={'icon-bzm-clipboard'}></BzmIcon>
                </Button>
              </div>
            </Popover>
          }
        >
          <button
            type="button"
            title={i18n.t('servicePicker.showServiceId')}
            id={iconId}
            className={clsx(
              styles.serviceItemIcons,
              'btn btn-link btn-xs btn-no-hover form-inline mr-2 serviceItemIcons',
            )}
            onClick={() => {
              this.state.serviceIconSelected !== undefined && this.state.serviceIconSelected.key === serviceId
                ? this.setState({ serviceIconSelected: undefined })
                : this.setState({ serviceIconSelected: { key: serviceId, value: iconId } });
            }}
          >
            <BzmIcon variant={'icon-bzm-id-circle'} className={'icon-xs'} />
          </button>
        </OverlayTrigger>
      </>
    );
  };

  cloneService = (event: any, service: Service) => {
    const clonedAt = i18n.t('servicePicker.clonedAt');
    this.setState({
      clonedServiceName: service.name + clonedAt + formatDateTime(new Date()),
      modalSelected: service,
      isCloneServiceModalOpen: true,
      keepDropdownOpen: false,
    });
  };

  exportService = (event: any, service: Service) => {
    this.setState({ modalSelected: service });
    this.props.exportService(this.props.services, service);
  };

  getDeletedButtonDom = (service: Service) => {
    if (!this.props.isMaintenanceEnabled) {
      return null;
    }

    const isProtected = isServiceProtected(service.name, this.props.blazeUserConfig);

    return (
      <span
        title={
          isProtected ? i18n.t('servicePicker.serviceProtectedForDeletion') : i18n.t('servicePicker.deleteService')
        }
      >
        <button
          id={`sv-delete-${service.id}`}
          disabled={isProtected}
          className={clsx(
            styles.serviceItemIcons,
            'btn btn-link btn-xs btn-no-hover form-inline mr-2 serviceItemIcons',
          )}
          onClick={() => this.deleteServiceHandler(service)}
        >
          <BzmIcon variant={'icon-bzm-delete'} className={'icon-xs'} />
        </button>
      </span>
    );
  };

  getCreateBlueprintButtonDom = (service: Service) => {
    if (!this.props.onCreateBlueprint) return null;
    return (
      <button
        id={`sv-create-blueprint-${service.id}`}
        type="button"
        className={clsx(
          styles.serviceItemIcons,
          'btn btn-link btn-xs btn-no-hover form-inline mr-2 serviceItemIcons text-dark',
        )}
        title={i18n.t('blueprints:servicePicker.createBlueprint')}
        onClick={(event: any) => {
          event.preventDefault();
          event.stopPropagation();
          this.setState({ keepDropdownOpen: false });
          this.props.onCreateBlueprint!(service);
        }}
      >
        <BzmIcon variant={'icon-bzm-add'} className={'icon-xs'} />
      </button>
    );
  };

  getCloneButtonDom = (service: Service) => {
    if (!this.props.isCloneEnabled) {
      return null;
    }
    return (
      <button
        id={`sv-clone-${service.id}`}
        className={clsx(
          styles.serviceItemIcons,
          'btn btn-link btn-xs btn-no-hover form-inline mr-2 serviceItemIcons text-dark',
        )}
        title={i18n.t('servicePicker.cloneService')}
        onClick={(event: any) => {
          this.cloneService(event, service);
        }}
      >
        <BzmIcon variant={'icon-bzm-copy'} className={'icon-xs'}></BzmIcon>
      </button>
    );
  };

  getExportImportButtonDom = (service: Service, action: 'import' | 'export') => {
    if (!this.props.isMaintenanceEnabled) {
      return null;
    }

    if (!this.props.isCloneEnabled) {
      return null;
    }
    return (
      <button
        id={`sv-export-${service.id}`}
        className={clsx(
          styles.serviceItemIcons,
          'btn btn-link btn-xs btn-no-hover form-inline mr-2 serviceItemIcons text-dark',
        )}
        title={action === 'export' ? i18n.t('servicePicker.exportService') : i18n.t('servicePicker.importService')}
        onClick={(event: any) => {
          if (action === 'export') {
            MixpanelService.track(EXPORT_SERVICE_TO_ZIP());
            this.exportService(event, service);
          }

          //Open the file chooser
          if (action === 'import') {
            this.setState({ importedService: service }, () => {
              this.fileUploadRef.current.click();
              MixpanelService.track(IMPORT_SERVICE_FROM_ZIP());
            });
          }
        }}
      >
        {action === 'export' ? (
          <BzmIcon variant={'icon-bzm-ct-export'} className={'icon-xs'} />
        ) : (
          <BzmIcon variant={'icon-bzm-ct-import'} className={'icon-xs'} />
        )}
      </button>
    );
  };

  getRenameButtonDom = (service: Service) => {
    if (!this.props.isMaintenanceEnabled) {
      return null;
    }

    const isProtected = isServiceProtected(service.name, this.props.blazeUserConfig);

    return (
      <span
        title={
          isProtected ? i18n.t('servicePicker.serviceProtectedForRenaming') : i18n.t('servicePicker.renameService')
        }
      >
        <button
          id={`sv-edit-${service.id}`}
          disabled={isProtected}
          className={clsx(
            styles.serviceItemIcons,
            'btn btn-link btn-xs btn-no-hover form-inline mr-2 serviceItemIcons',
          )}
          onClick={(event: any) => {
            this.editService(event, service);
          }}
        >
          <BzmIcon variant={'icon-bzm-edit'} className={'icon-xs'} />
        </button>
      </span>
    );
  };

  getServiceItem = (sName: string, service: Service) => {
    return (
      <div style={{ minHeight: '24px', lineHeight: '24px' }} className={'d-flex'}>
        <div style={{ width: '100%' }} className="text-truncate">
          <span dangerouslySetInnerHTML={{ __html: sName }} />
        </div>
        <div className="ml-auto">
          <span
            className={clsx('hover-actions')}
            style={
              this.state.serviceIconSelected !== undefined && this.state.serviceIconSelected.key === service.id
                ? { display: 'block' }
                : this.state.serviceIconSelected === undefined
                ? undefined
                : { display: 'none' }
            }
            onClick={event => {
              //to avoid trigger 'click on the popover' which sets show=true for the dropdown
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {this.getRenameButtonDom(service)}
            {this.getExportImportButtonDom(service, 'import')}
            {this.getExportImportButtonDom(service, 'export')}
            {this.getCloneButtonDom(service)}
            {this.getServiceIdDom(service.id ? service.id : 0)}
            {this.getCreateBlueprintButtonDom(service)}
            {this.getDeletedButtonDom(service)}
          </span>
        </div>
      </div>
    );
  };
  /**
   * Creates drop down items with services. It also puts ones in the bold when there is a
   * match with searchterm.
   */
  createServiceItems = () => {
    const serviceItems = this.getFilteredServices().map((service: Service) => {
      let serviceName = service.name;
      if (this.state.searchTerm !== '') {
        const searchTextRegExp = new RegExp(this.state.searchTerm, 'i'); //  case insensitive regexp
        serviceName = service.name.replace(searchTextRegExp, '<b>$&</b>');
      }

      return (
        <Dropdown.Item
          title={serviceName}
          onClick={(event: any) => this.selectService(event, service)}
          key={service.id}
          className="action-item"
        >
          {this.getServiceItem(serviceName, service)}
        </Dropdown.Item>
      );
    });

    //Push All Services placeholder to beginning
    if (this.props.isSelectAll === true && this.state.searchTerm === '' && serviceItems.length !== 0) {
      serviceItems.unshift(
        <Dropdown.Item onClick={(event: any) => this.selectService(event, null)} key="0">
          {this.state.selectPlaceholder}
        </Dropdown.Item>,
      );
    }

    return serviceItems;
  };

  getId = (id: string) => {
    const name = this.state.selected != null ? (this.state.selected as Service).name : this.state.selectPlaceholder;
    return id + '\\' + name;
  };

  postModalClose(keepModalOpen: boolean) {
    if (keepModalOpen === true) {
      // if modal remains open we keep the selected option.
      this.setState({ isServiceModalOpen: keepModalOpen, serviceIconSelected: undefined });
    } else {
      // if modal close we reset the options.
      this.setState({
        isServiceModalOpen: keepModalOpen,
        serviceIconSelected: undefined,
        modalSelected: null,
        newServiceModal: false,
      });
    }
  }

  cloneModalClose(keepModalOpen: boolean) {
    this.setState({ isCloneServiceModalOpen: keepModalOpen });
  }

  handleToggle = (isOpen: boolean) => {
    if (this.state.serviceIconSelected !== undefined) {
      if (this.state.clickOnPopoverEvent === false) {
        this.handlePopover(this.state.serviceIconSelected.value); //this will also put serviceIconSelected to undefined
        this.setState({ keepDropdownOpen: isOpen });
      }
    } else {
      this.setState({ keepDropdownOpen: isOpen });
    }
    this.setState({ clickOnPopoverEvent: false });
  };

  getFileUploader = () => (
    <input
      type="file"
      accept="application/zip"
      onChange={(event: any) => {
        const file = event.target.files[0];
        this.props.importService(this.state.importedService, file);
      }}
      ref={this.fileUploadRef}
      style={{ display: 'none' }} // Make the file input element invisible
    />
  );

  render() {
    const { t } = this.props;
    const isValid = this.state.isServiceNameValid ? '' : 'is-invalid';
    const serviceItems = this.createServiceItems();

    const loadingIndicator = (
      <div className="text-center p-5">
        <SimpleLoader loadingText={t('actions.loading')} />
      </div>
    );

    const footer = this.createDropDownFooter(serviceItems);

    let searchElement;
    const placeHolderText = this.props.isCreateEnabled
      ? t('servicePicker.searchOrServiceTitle')
      : t('servicePicker.searchTitle');
    //There are results which can be search through
    if (this.props.services.length > 0) {
      searchElement = (
        <div className="has-form-control-icon has-form-control-icon-left mb-3">
          <FormControl
            className={isValid}
            autoFocus
            placeholder={placeHolderText}
            style={{ width: '100%' }}
            aria-label={t('actions.search')}
            onChange={this.searchChangedHandler}
            ref={this.searchInputRef}
            value={this.state.searchTerm}
          />
          <span className="form-control-icon form-control-icon-left">
            <BzmIcon variant={'icon-bzm-search'} className={'icon-theme-gray-50'} />
            <span className="sr-only">{t('actions.search')}</span>
          </span>
        </div>
      );
    } else {
      //No results, create is enabled
      if (this.props.isCreateEnabled === true) {
        searchElement = (
          <FormControl
            className={isValid}
            autoFocus
            placeholder={t('servicePicker.enterNameOfTheService')}
            style={{ width: '100%' }}
            aria-label={t('actions.search')}
            onChange={this.searchChangedHandler}
            ref={this.searchInputRef}
          />
        );
      }
    }

    const popperConfig = this.props.popperConfig ? this.props.popperConfig : DEFAULT_POPPER_CONFIG;

    return (
      <>
        {(this.state.modalSelected != null || this.state.newServiceModal === true) && this.state.isServiceModalOpen && (
          <EditServiceModal
            service={this.state.newServiceModal === true ? null : this.state.modalSelected}
            closeModalAction={(keepModalOpen: boolean) => this.postModalClose(keepModalOpen)}
            createServiceCallBack={(service: Service) => this.createServiceCallBack(service)}
          />
        )}

        {this.state.modalSelected !== null && this.state.isCloneServiceModalOpen && (
          <CloneServiceModal
            service={this.state.modalSelected}
            closeModalAction={(keepModalOpen: boolean) => this.cloneModalClose(keepModalOpen)}
            clonedServiceName={this.state.clonedServiceName}
          />
        )}

        <Dropdown onToggle={this.handleToggle} show={this.state.keepDropdownOpen}>
          <Dropdown.Toggle
            disabled={this.props.selectionDisabled}
            id={this.getId(this.props.id ? this.props.id : 'servicePicker')}
            as={ServiceToggle}
            {...{
              handleFocus: this.handleFocus,
              focus: this.props.focus,
            }}
          >
            {this.state.selected != null
              ? getShortString((this.state.selected as Service).name, 30)
              : this.state.selectPlaceholder}
          </Dropdown.Toggle>
          <Dropdown.Menu style={this.pickerMenuStyles} popperConfig={popperConfig}>
            {searchElement}
            {this.props.isLoading && loadingIndicator}
            <div
              className="dropdown-menu-limited"
              style={{
                maxHeight: this.props.maxHeight ? this.props.maxHeight : undefined,
              }}
            >
              {serviceItems}
            </div>
            {footer}

            {/* File Upload component for Import Service */}
            {this.getFileUploader()}
          </Dropdown.Menu>
        </Dropdown>
      </>
    );
  }
}

const mapStateToProps = (state: ApplicationState) => ({
  services: state.services.services,
  isLoading: state.services.isLoading,
  blazeUserConfig: state.blazeUserConfig.blazeUserConfig,
});

// Bound with dispatch, so we can reuse the promise created by createService call
const mapDispatchToProps = (dispatch: any) => ({
  fetchVirtualServices: (forceRefresh?: boolean) => dispatch(fetchServices(forceRefresh)),
  createService: (serviceName: string) => dispatch(createServiceThunk(serviceName)),
  exportService: (services: Service[], service: Service) => dispatch(exportService(services, service)),
  importService: (service: Service, file: File) => dispatch(importService(service, file)),
  deleteService: (services: Service[], service: Service, selectedServiceId: number | undefined) =>
    dispatch(deleteService(services, service, selectedServiceId)),
  showToastNotification: (type: ToastType, message: string) => dispatch(showToastNotification(type, message)),
});

export default withTranslation('common')(connect(mapStateToProps, mapDispatchToProps)(ServicePicker));
